/**
 * @fileoverview Provide the weekly timed/all-day layout for caller-supplied calendar records. `kc-internal-frontend/src/components/Calendar/BespokeCalendar.js` owns navigation; shared calendar utilities and EventComponent own time/layout conventions and action signifiers. The view delegates event/day selection without owning event persistence or authorization.
 */

import React, { useMemo, useState } from 'react';
import moment from 'moment-timezone';
import EventComponent from '../components/EventComponent';
import AppIcon from '../../shared/AppIcon';
import { 
  generateWeekDays, 
  generateHourSlots, 
  isToday, 
  calculateEventOverlaps,
  groupEventsByDay,
  CALENDAR_VIEWS,
  formatTime 
} from '../utils/calendarUtils';

const MAX_COLLAPSED_ALL_DAY_ROWS = 3;
const ALL_DAY_ROW_HEIGHT = 26;
const ALL_DAY_SECTION_PADDING = 8;



const WeekView = ({ 
  currentDate, 
  events = [],
  onEventClick,
  onEventEdit,
  onEventDelete,
  onConfirmResponsibility,
  onTimeSlotClick,
  onEventDrop,
  assignees = [],
  currentUser = null
}) => {
  const [isAllDayExpanded, setIsAllDayExpanded] = useState(false);
  // Generate week days and hour slots
  const weekDays = useMemo(() => generateWeekDays(currentDate), [currentDate]);
  const hourSlots = useMemo(() => generateHourSlots(), []);
  
  // Group events by day for the week
  const eventsByDay = useMemo(() => {
    const startDate = weekDays[0];
    const endDate = weekDays[6];
    return groupEventsByDay(events, startDate, endDate);
  }, [events, weekDays]);

  // Get events for a specific day with overlap calculations
  const getEventsForDay =

  (date) => {
    const dateKey = date.format('YYYY-MM-DD');
    const dayEvents = eventsByDay[dateKey] || [];
    
    // Filter out all-day events (they'll be rendered separately)
    const timedEvents = dayEvents.filter(event => !event.allDay);
    
    // Calculate overlaps for proper positioning
    return calculateEventOverlaps(timedEvents);
  };

  const allDayEventsByDay = useMemo(() => (
    weekDays.map(date => {
      const dateKey = date.format('YYYY-MM-DD');
      const dayEvents = eventsByDay[dateKey] || [];

      return {
        date,
        dateKey,
        events: dayEvents.filter(event => event.allDay)
      };
    })
  ), [eventsByDay, weekDays]);

  // Calculate event style for week view positioning
  const calculateEventStyle =

  (event, dayIndex) => {
    const eventStart = moment(event.start);
    const eventEnd = moment(event.end);
    
    // Calculate position
    const startHour = eventStart.hour() + (eventStart.minute() / 60);
    const endHour = eventEnd.hour() + (eventEnd.minute() / 60);
    const duration = endHour - startHour;
    
    // Time slot height in pixels (each hour is 60px)
    const hourHeight = 60;
    const top = startHour * hourHeight;
    const height = Math.max(duration * hourHeight, 30); // Minimum 30px height
    
    // Handle overlapping events
    const overlapAdjustment = event.totalOverlapping > 1 ? 
      (100 / event.totalOverlapping) : 100;
    const leftOffset = event.overlapIndex * overlapAdjustment;
    
    return {
      top: `${top}px`,
      height: `${height}px`,
      left: `calc(${leftOffset}% + 2px)`,
      width: `calc(${overlapAdjustment}% - 4px)`,
      zIndex: 10 + event.overlapIndex
    };
  };

  // Handle time slot click for creating new events
  const handleTimeSlotClick =

  (date, hour, e) => {
    e.stopPropagation();
    if (onTimeSlotClick) {
      const clickTime = moment(date).hour(Math.floor(hour)).minute((hour % 1) * 60);
      onTimeSlotClick(clickTime);
    }
  };

  // Handle event drop
  const handleEventDrop =

  (event, dropDate, dropHour) => {
    if (onEventDrop) {
      const newDateTime = moment(dropDate).hour(Math.floor(dropHour)).minute((dropHour % 1) * 60);
      onEventDrop(event, newDateTime);
    }
  };

  // Render week header with dates
  const renderWeekHeader =

  () => {
    return (
      <div className="week-view-header">
        <div className="time-gutter-header"></div>
        {weekDays.map((date, index) => {
          const isTodayDate = isToday(date);
          return (
            <div 
              key={index} 
              className={`week-day-header ${isTodayDate ? 'today' : ''}`}
            >
              <div className="day-name">{date.format('ddd')}</div>
              <div className="day-number">{date.format('D')}</div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render all-day events section
  const renderAllDayEvents =

  () => {
    const maxEventsInDay = allDayEventsByDay.reduce(
      (maxEvents, day) => Math.max(maxEvents, day.events.length),
      0
    );
    const hiddenEventCount = allDayEventsByDay.reduce(
      (count, day) => count + Math.max(0, day.events.length - MAX_COLLAPSED_ALL_DAY_ROWS),
      0
    );
    const hasOverflow = hiddenEventCount > 0;

    if (maxEventsInDay === 0) return null;

    const visibleRows = isAllDayExpanded
      ? maxEventsInDay
      : Math.min(maxEventsInDay, MAX_COLLAPSED_ALL_DAY_ROWS) + (hasOverflow ? 1 : 0);
    const contentHeight = Math.max((visibleRows * ALL_DAY_ROW_HEIGHT) + ALL_DAY_SECTION_PADDING, 44);
    const weekStart = weekDays[0].format('YYYY-MM-DD');
    const allDayEventsId = `week-all-day-events-${weekStart}`;
    const toggleLabel = isAllDayExpanded
      ? 'Collapse all-day events'
      : `Show ${hiddenEventCount} more all-day event${hiddenEventCount === 1 ? '' : 's'}`;

    return (
      <div
        id={allDayEventsId}
        className={`all-day-section week-all-day-section ${isAllDayExpanded ? 'expanded' : 'collapsed'}`}
        style={{ '--week-all-day-content-height': `${contentHeight}px` }}
      >
        <div className="time-gutter week-all-day-label">
          <span className="all-day-label">All Day</span>
          <span className="all-day-count">
            {allDayEventsByDay.reduce((count, day) => count + day.events.length, 0)} total
          </span>
          {hasOverflow && (
            <button
              type="button"
              className="all-day-toggle"
              aria-expanded={isAllDayExpanded}
              aria-controls={allDayEventsId}
              onClick={() => setIsAllDayExpanded((expanded) => !expanded)}
            >
              <AppIcon icon={isAllDayExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} aria-hidden="true" />
              <span>{toggleLabel}</span>
            </button>
          )}
        </div>
        {allDayEventsByDay.map(({ date, dateKey, events: allDayEvents }, dayIndex) => {
          const visibleEvents = isAllDayExpanded
            ? allDayEvents
            : allDayEvents.slice(0, MAX_COLLAPSED_ALL_DAY_ROWS);
          const hiddenCount = Math.max(0, allDayEvents.length - MAX_COLLAPSED_ALL_DAY_ROWS);

          return (
            <div key={dateKey} className="all-day-column">
              {visibleEvents.map((event, eventIndex) => (
                <EventComponent
                  key={`${event.id}-allday`}
                  event={event}
                  viewType={CALENDAR_VIEWS.WEEK}
                  isCompact={true}
                  style={{
                    position: 'absolute',
                    top: `${eventIndex * ALL_DAY_ROW_HEIGHT}px`,
                    left: '2px',
                    right: '2px',
                    height: '22px'
                  }}
                  onEventClick={onEventClick}
                  onEventEdit={onEventEdit}
                  onEventDelete={onEventDelete}
                  onConfirmResponsibility={onConfirmResponsibility}
                  assignees={assignees}
                  currentUser={currentUser}
                />
              ))}
              {hiddenCount > 0 && !isAllDayExpanded && (
                <button
                  type="button"
                  className="more-events-indicator all-day-more-button"
                  style={{ top: `${MAX_COLLAPSED_ALL_DAY_ROWS * ALL_DAY_ROW_HEIGHT}px` }}
                  aria-label={`Show ${hiddenCount} more all-day events for ${date.format('dddd, MMMM D')}`}
                  onClick={() => setIsAllDayExpanded(true)}
                >
                  +{hiddenCount} more
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Render time grid
  const renderTimeGrid =

  () => {
    return (
      <div className="week-time-grid">
        {hourSlots.map((hourSlot, hourIndex) => {
          const hour = hourIndex;
          
          return (
            <div key={hourIndex} className="time-slot-row">
              <div className="time-gutter">
                <span className="time-label">{formatTime(hourSlot)}</span>
              </div>
              
              {weekDays.map((date, dayIndex) => (
                <div
                  key={dayIndex}
                  className="time-slot"
                  onClick={(e) => handleTimeSlotClick(date, hour, e)}
                  onDrop={(e) => {
                    e.preventDefault();
                    const eventData = JSON.parse(e.dataTransfer.getData('application/json'));
                    handleEventDrop(eventData, date, hour);
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  data-date={date.format('YYYY-MM-DD')}
                  data-hour={hour}
                >
                  {/* Half-hour divider */}
                  <div className="half-hour-line"></div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  // Render events for each day column
  const renderDayEvents =

  () => {
    return (
      <div className="week-events-overlay">
        <div className="time-gutter-overlay"></div>
        {weekDays.map((date, dayIndex) => {
          const dayEvents = getEventsForDay(date);
          
          return (
            <div key={dayIndex} className="day-events-column">
              {dayEvents.map((event) => (
                                 <EventComponent
                   key={event.id}
                   event={event}
                   viewType={CALENDAR_VIEWS.WEEK}
                   isDraggable={true}
                   style={calculateEventStyle(event, dayIndex)}
                   onEventClick={onEventClick}
                   onEventEdit={onEventEdit}
                   onEventDelete={onEventDelete}
                   onConfirmResponsibility={onConfirmResponsibility}
                   assignees={assignees}
                   currentUser={currentUser}
                 />
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="week-view">
      {renderWeekHeader()}
      {renderAllDayEvents()}
      <div className="week-content">
        {renderTimeGrid()}
        {renderDayEvents()}
      </div>
    </div>
  );
};

export default WeekView; 
